import { createTheme } from '@mui/material/styles';
import { alpha, PaletteMode } from '@mui/material';

// After Hours design tokens, shared with the iOS reskin (header is web-only). Keep
// this table in step with docs/after-hours-design-tokens.md when anything changes.
export const afterHoursTokens = {
  dark: {
    ink: '#0D1114', // page background
    header: '#080D11', // header, footer and hero fallback
    surface: '#141C23', // cards and dialogs
    raised: '#18212A', // notes, inputs, selected states
    line: '#2C3339', // dividers and card borders
    text: '#F8F9FB',
    muted: '#B2BAC2',
    action: '#096CDD', // primary buttons
    actionHover: '#075BC1',
    actionEdge: '#3B98FF', // 1px border on primary buttons
    link: '#2D9BFF', // text links and highlights
    progress: '#A666FF', // progress lines and focus rings
    success: '#97D2B7',
    warning: '#F5B84A',
    danger: '#FF8A95',
  },
  light: {
    ink: '#F4F6F8',
    header: '#FFFFFF',
    surface: '#FFFFFF',
    raised: '#E9EDF1',
    line: '#D9DEE3',
    text: '#0D1114',
    muted: '#4A5560',
    action: '#096CDD',
    actionHover: '#075BC1',
    actionEdge: '#3B98FF',
    link: '#0A5FC2',
    progress: '#7339D0',
    success: '#1C7148',
    warning: '#8A5A00',
    danger: '#B3261E',
  },
} as const;

export const displayFont = '"Barlow Condensed", "Arial Narrow", Impact, sans-serif';
export const bodyFont = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const createJuggleVisionTheme = (mode: PaletteMode) => {
  const t = afterHoursTokens[mode];
  return createTheme({
    palette: {
      mode,
      // primary.main colours text, icons and selected tabs, so it is the AA link blue;
      // filled buttons and chips use the action blue through the overrides below.
      // Labels on primary/secondary fills are ink in dark mode, where white fails AA.
      primary: {
        main: t.link,
        light: t.link,
        dark: t.actionHover,
        contrastText: mode === 'dark' ? t.ink : '#FFFFFF',
      },
      secondary: {
        main: t.progress,
        light: '#C39BFF',
        dark: '#6A33CC',
        contrastText: mode === 'dark' ? t.ink : '#FFFFFF',
      },
      background: {
        default: t.ink,
        paper: t.surface,
      },
      text: {
        primary: t.text,
        secondary: t.muted,
      },
      divider: t.line,
      // Status colours from the shared set: each passes 4.5:1 as text on every surface in its mode.
      success: {
        main: t.success,
      },
      warning: {
        main: t.warning,
      },
      error: {
        main: t.danger,
      },
    },
    typography: {
      fontFamily: bodyFont,
      h1: { fontFamily: displayFont, fontWeight: 800, lineHeight: 0.94, letterSpacing: '-0.01em' },
      h2: { fontFamily: displayFont, fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.005em' },
      h3: { fontFamily: displayFont, fontWeight: 700, lineHeight: 1.1 },
      h4: { fontFamily: displayFont, fontWeight: 700, lineHeight: 1.15 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      overline: { fontWeight: 500, fontSize: '0.75rem', letterSpacing: '0.32em', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.ink,
            color: t.text,
            scrollbarColor: `${t.line} ${t.ink}`,
          },
          '::-webkit-scrollbar-track': { background: t.ink },
          '::-webkit-scrollbar-thumb': { background: t.line },
          '::selection': {
            backgroundColor: alpha(t.link, 0.3),
          },
          'a:focus-visible, button:focus-visible, [role="button"]:focus-visible': {
            outline: `3px solid ${t.progress}`,
            outlineOffset: 3,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // MUI lightens dark-mode paper with a gradient; After Hours uses flat surfaces.
          root: { backgroundImage: 'none' },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'inherit',
        },
        styleOverrides: {
          root: {
            backgroundColor: t.header,
            color: t.text,
            borderBottom: `1px solid ${t.line}`,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            border: `1px solid ${t.line}`,
            borderRadius: 12,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 9,
            textTransform: 'none',
            fontWeight: 600,
          },
          containedPrimary: {
            backgroundColor: t.action,
            color: '#FFFFFF',
            border: `1px solid ${t.actionEdge}`,
            '&:hover': { backgroundColor: t.actionHover },
          },
          outlinedPrimary: {
            borderColor: alpha(t.text, 0.28),
            color: t.text,
            '&:hover': { borderColor: t.text, backgroundColor: alpha(t.text, 0.06) },
          },
          sizeLarge: {
            minHeight: 56,
            padding: '14px 28px',
            fontSize: '0.9375rem',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 },
          filledPrimary: { backgroundColor: t.action, color: '#FFFFFF' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${t.line}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${t.line}`,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: t.link },
        },
      },
    },
  });
};

export default createJuggleVisionTheme;
